import React, { useState, useEffect } from 'react';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import api, { getApiBase } from './utils/api';

// Import components
import Navigation from './components/Navigation';
import FaceScanner from './components/FaceScanner';
import Login from './components/Login';
import MyReports from './components/MyReports';
import AdminDashboard from './components/AdminDashboard';
import EmployeeManagement from './components/EmployeeManagement';
import DailyReport from './components/DailyReport';
import LateReport from './components/LateReport';
import LeaveReport from './components/LeaveReport';
import SummaryReport from './components/SummaryReport';
import OrganizationSettings from './components/OrganizationSettings';
import MyProfile from './components/MyProfile';
import HolidayManagement from './components/HolidayManagement';
import LeaveRequestForm from './components/LeaveRequestForm'; 
import LeaveApproval from './components/LeaveApproval';
import LateRequestForm from './components/LateRequestForm';
import ForgotScanRequestForm from './components/ForgotScanRequestForm';
import LateApproval from './components/LateApproval'; 
import ForgotScanApproval from './components/ForgotScanApproval'; 
import DepartmentManagement from './components/DepartmentManagement'; 
import PermissionLeaveForm from './components/PermissionLeaveForm';
import Logs from './components/Logs';
import PermissionReport from './components/PermissionReport';
import OfficialBusinessManagement from './components/OfficialBusinessManagement';
import OfficialBusinessReport from './components/OfficialBusinessReport';
import AttendanceManager from './components/AttendanceManager';
import ExtraWorkDaysManagement from './components/ExtraWorkDaysManagement';

// คอมโพเนนต์ Super Admin
import SuperAdminDashboard from './components/SuperAdminDashboard'; 

import { Camera } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';

// Component: หน้าจอแจ้งเตือนไม่มีเน็ต
const NoInternetScreen = () => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 text-white">
    <div className="text-center p-8">
      <div className="mb-4 flex justify-center">
        <svg className="w-20 h-20 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold mb-2">ไม่พบสัญญาณอินเทอร์เน็ต</h2>
      <p className="text-gray-400">กรุณาตรวจสอบการเชื่อมต่อ Wi-Fi หรือข้อมูลมือถือของคุณ</p>
    </div>
  </div>
);

const App = () => {
  // [✅ แก้ไข] ค่าเริ่มต้นคือหน้า Login
  const [currentPage, setCurrentPage] = useState('login');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [appReady, setAppReady] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const setStatusBar = async () => {
      try {
        await StatusBar.setStyle({ style: 'DARK' }); 
        await StatusBar.setBackgroundColor({ color: '#1F2937' });
      } catch (err) {
        console.log('StatusBar error:', err);
      }
    };

    setStatusBar();
    setTimeout(setStatusBar, 500);
  }, []);

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const cameraStatus = await Camera.checkPermissions();
        if (cameraStatus.camera !== 'granted') {
           await Camera.requestPermissions();
        }
        const geoStatus = await Geolocation.checkPermissions();
        if (geoStatus.location !== 'granted') {
           await Geolocation.requestPermissions();
        }
      } catch (error) {
        console.error("Error requesting permissions:", error);
      }
    };
    checkPermissions();
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    setAppReady(true);
  }, []);

  useEffect(() => {
    if (!isOnline) return;

    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user_data');
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        
        // [✅ แก้ไข] เปลี่ยนหน้าตามสิทธิ์ หลังจากจำได้ว่าเข้าระบบอยู่
        if (parsedUser.is_super_admin) {
          setCurrentPage('super-admin-dashboard');
        } else if (parsedUser.is_admin || parsedUser.is_staff) {
          setCurrentPage('admin-dashboard');
        } else {
          setCurrentPage('face-scanner');
        }
      } catch (error) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        setCurrentPage('login'); // กลับไปหน้าล็อกอินถ้า Token พัง
      }
    } else {
      setCurrentPage('login'); // ถ้าไม่มีข้อมูลในเครื่องให้บังคับเข้าล็อกอิน
    }
  }, [isOnline]);

  // ==========================================
  // ระบบ Push Notifications
  // ==========================================
  useEffect(() => {
    // เช็คก่อนว่าเป็นแอปบนมือถือจริงๆ (ไม่ใช่เปิดบนเบราว์เซอร์คอมพิวเตอร์)
    if (Capacitor.isNativePlatform()) {
      const initPushNotifications = async () => {
        try {
          // 👉 เพิ่มโค้ดส่วนนี้: สร้าง Channel สำหรับ Android ให้มีความสำคัญระดับสูงสุด (5)
          if (Capacitor.getPlatform() === 'android') {
            await PushNotifications.createChannel({
              id: 'high_priority_channel', // ชื่อ ID ต้องตรงกับหลังบ้าน
              name: 'การแจ้งเตือนด่วน',
              description: 'สำหรับการแจ้งเตือนแบบลอยบนหน้าจอ',
              importance: 5, // 5 = High importance (บังคับให้ลอย)
              visibility: 1, // 1 = Public (แสดงบนหน้าจอล็อก)
              vibration: true,
            });
          }
          // 1. ขอสิทธิ์ส่งการแจ้งเตือนจากผู้ใช้ (จะเด้ง Popup ถาม)
          const permStatus = await PushNotifications.requestPermissions();

          if (permStatus.receive === 'granted') {
            // 2. ถ้าผู้ใช้อนุญาต ให้ลงทะเบียนเครื่องนี้เข้ากับระบบ Firebase
            await PushNotifications.register();
          } else {
            console.log('ผู้ใช้ไม่อนุญาตให้ส่งการแจ้งเตือน');
          }

          // 3. ดักรับ Token และส่งเข้า Database ทันที
          PushNotifications.addListener('registration', async (token) => {
            const userData = localStorage.getItem('user_data');
            if (userData) {
              try {
                const user = JSON.parse(userData);
                
                // ใช้ api.savePushSubscription ที่เราแก้ใหม่ด้านบนแทนการ fetch เอง
                await api.savePushSubscription(token.value, user.employee_id);
                
                console.log('✅ บันทึก FCM Token ลงฐานข้อมูลสำเร็จ');
              } catch (err) {
                console.error('❌ ส่ง FCM Token ไปฐานข้อมูลไม่สำเร็จ:', err);
              }
            }
          });

          // กรณีลงทะเบียนไม่สำเร็จ
          PushNotifications.addListener('registrationError', (error) => {
            console.error('❌ สมัครรับแจ้งเตือนไม่สำเร็จ:', error);
          });

          // 4. ทำงานเมื่อมีแจ้งเตือนเข้ามา (ตอนที่กำลังเปิดแอปเล่นอยู่)
          PushNotifications.addListener('pushNotificationReceived', (notification) => {
            console.log('🔔 มีแจ้งเตือนเข้า:', notification);
            // สามารถเขียนโค้ดให้ Alert โชว์ในแอปได้ตรงนี้
          });

          // 5. ทำงานเมื่อผู้ใช้กดจิ้มที่แถบแจ้งเตือนด้านบน
          PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
            console.log('👉 ผู้ใช้กดที่แจ้งเตือน:', notification);
            // เขียนโค้ดให้กระโดดไปหน้าต่างๆ ได้ เช่น พาไปหน้ารายงาน
          });

        } catch (error) {
          console.error('Error initializing Push Notifications:', error);
        }
      };

      initPushNotifications();
    }
  }, []);

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    setUser(null);
    // [✅ แก้ไข] กดออกจากระบบให้เด้งไปหน้า login
    setCurrentPage('login');
    setShowMobileMenu(false);
  };

  if (!appReady) return <div className="min-h-screen bg-blue-500 flex items-center justify-center text-white">กำลังเริ่มต้นระบบ...</div>;
  if (!isOnline) return <NoInternetScreen />;

  const isStaffOrAdmin = user?.is_admin || user?.is_staff;
  const noAccessComponent = (
    <div className="text-center py-12">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">ไม่มีสิทธิ์เข้าถึง</h2>
      <button onClick={() => setCurrentPage('login')} className="text-blue-500">กลับหน้าล็อกอิน</button>
    </div>
  );

  const renderCurrentPage = () => {
    // [✅ เพิ่ม] ดักจับผู้ใช้ที่ยังไม่เข้าระบบให้ไปหน้าล็อกอิน (ยกเว้นกำลังอยู่ที่หน้าล็อกอิน)
    if (!user && currentPage !== 'login') {
       return <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
    }

    switch (currentPage) {
      case 'login': return user ? <FaceScanner /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      // [✅ แก้ไข] หน้า FaceScanner ถูกล็อก ต้องมี user เท่านั้นถึงเข้าได้
      case 'face-scanner': return user ? <FaceScanner /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      
      case 'my-reports': return user ? <MyReports user={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      case 'my-profile': return user ? <MyProfile user={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      case 'leave-request': return user ? <LeaveRequestForm currentUser={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      case 'late-request': return user ? <LateRequestForm currentUser={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      case 'forgot-scan-request': return user ? <ForgotScanRequestForm currentUser={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      case 'permission-leave': return user ? <PermissionLeaveForm currentUser={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      
      case 'super-admin-dashboard': return user?.is_super_admin ? <SuperAdminDashboard user={user} /> : noAccessComponent;

      case 'holidays': return isStaffOrAdmin ? <HolidayManagement /> : noAccessComponent;
      case 'department-management': return isStaffOrAdmin ? <DepartmentManagement user={user} /> : noAccessComponent;
      case 'leave-approval': return isStaffOrAdmin ? <LeaveApproval user={user} /> : noAccessComponent;
      case 'late-approval': return isStaffOrAdmin ? <LateApproval user={user} /> : noAccessComponent;
      case 'forgot-scan-approval': return isStaffOrAdmin ? <ForgotScanApproval user={user} /> : noAccessComponent;
      case 'admin-dashboard': return isStaffOrAdmin ? <AdminDashboard setCurrentPage={setCurrentPage} user={user} /> : noAccessComponent;
      case 'employees': return isStaffOrAdmin ? <EmployeeManagement user={user} /> : noAccessComponent;
      case 'logs': return isStaffOrAdmin ? <Logs /> : noAccessComponent;
      case 'daily-report': return isStaffOrAdmin ? <DailyReport user={user} /> : noAccessComponent;
      case 'late-report': return isStaffOrAdmin ? <LateReport user={user} /> : noAccessComponent;
      case 'leave-report': return isStaffOrAdmin ? <LeaveReport user={user} /> : noAccessComponent;
      case 'permission-report': return isStaffOrAdmin ? <PermissionReport user={user} /> : noAccessComponent;
      case 'summary-report': return isStaffOrAdmin ? <SummaryReport user={user} /> : noAccessComponent;
      case 'settings': return isStaffOrAdmin ? <OrganizationSettings /> : noAccessComponent;
      case 'attendance-manager': return isStaffOrAdmin ? <AttendanceManager user={user} /> : noAccessComponent;
      case 'extra-work-days': return isStaffOrAdmin ? <ExtraWorkDaysManagement /> : noAccessComponent;
      case 'official-business': return <OfficialBusinessManagement currentUser={user} />;
      case 'official-business-report': return <OfficialBusinessReport user={user} />;
      default:
        if (!user) {
          return <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
        }
        if (user?.is_super_admin) {
          setCurrentPage('super-admin-dashboard');
          return <SuperAdminDashboard user={user} />;
        }
        if (isStaffOrAdmin) {
          setCurrentPage('admin-dashboard');
          return <AdminDashboard setCurrentPage={setCurrentPage} user={user} />;
        }
        setCurrentPage('face-scanner');
        return <FaceScanner />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 relative pb-8 pb-[env(safe-area-inset-bottom)]"> 
      <Navigation 
        user={user} 
        currentPage={currentPage} 
        setCurrentPage={setCurrentPage} 
        logout={logout}
        showMobileMenu={showMobileMenu}
        setShowMobileMenu={setShowMobileMenu}
      />
    
      <main className="container mx-auto px-4 py-8 mt-20 pt-[env(safe-area-inset-top)]">
        {renderCurrentPage()}
      </main>
    </div>
  );
};

export default App;